import React, { useState, useEffect, useRef } from "react";
import "../../styles/carousel.scss";
import { CircleOutlined } from "@mui/icons-material";
import Image from "next/legacy/image";

// Variants for 3D effect
export const CardVariants = deviceType => ({
  active: {
    scale: 1,
    opacity: 1,
    rotateY: 0,
    x: 0,
    zIndex: 100,
  },
  prev: {
    scale: 0.7,
    opacity: 0.7,
    // rotateY: 15,
    x: deviceType === "mobile" ? "-21%" : "-30%",
    zIndex: 0,
    transition: {
      duration: 0.4,
    },
  },
  next: {
    scale: 0.7,
    opacity: 0.7,
    // rotateY: -15,
    x: deviceType === "mobile" ? "21%" : "30%",
    zIndex: 0,
    transition: {
      duration: 0.4,
    },
  },
});

const ProgressBar = React.memo(
  ({ isActive, progress, setProgress, autoPlaySpeed, reverse }) => {
    const totalDuration = autoPlaySpeed;
    const increment = 100;
    useEffect(() => {
      if (!isActive) return;

      const totalStep = totalDuration / increment;
      let currentStep = 0;
      const interval = setInterval(() => {
        if (currentStep >= 100) {
          clearInterval(interval);
          setProgress(0);
        }
        currentStep += 1;
        const newProgress = (currentStep / totalStep) * 100;
        currentStep < totalStep && setProgress(newProgress);
      }, increment);
      return () => clearInterval(interval);
    }, []);

    return (
      <div
        style={{
          overflow: "hidden",
          width: "100%",
          height: 8,
          backgroundColor: reverse ? "#BCB2FF" : "#6934F3",
          borderRadius: "5px",
        }}
        className="4xl:!h-4"
      >
        <div
          style={{
            width: `${progress}%`,
            height: 8,
            background: reverse ? "#6934F3" : "#BCB2FF",
            transition: "0.1s ease-in-out",
            borderRadius: "5px",
          }}
          className={`rounded-2xl 4xl:!h-4`}
        />
      </div>
    );
  },
);
ProgressBar.displayName = "ProgressBar";
const CustomDot = React.memo(
  ({
    index,
    active,
    progress,
    setProgress,
    autoPlaySpeed,
    reverse = false,
    disableTimer,
  }) => {
    return (
      <li
        className={`custom-dots ${
          active ? "active " : "inactive"
        } flex justify-center items-center`}
      >
        {disableTimer ? (
          <CircleOutlined
            className={`!w-2 !h-2 4xl:!w-4 4xl:!h-4 !border-0 rounded-full my-1 ${
              reverse
                ? active
                  ? "text-onmoPurple-600 bg-onmoPurple-600"
                  : "text-onmoPurple-300 bg-onmoPurple-300"
                : active
                ? "text-onmoPurple-300 bg-onmoPurple-300"
                : "text-onmoPurple-600 bg-onmoPurple-600"
            }`}
          />
        ) : active ? (
          <div className="w-10 !h-2 !bg-onmoPurple-600 rounded-2xl 4xl:!h-4 4xl:w-14">
            <ProgressBar
              key={index}
              isActive={active}
              className=""
              progress={progress}
              setProgress={setProgress}
              autoPlaySpeed={autoPlaySpeed}
              reverse={reverse}
            />
          </div>
        ) : (
          <CircleOutlined className="!w-2 !h-2 4xl:!w-4 4xl:!h-4 text-onmoPurple-600 bg-onmoPurple-600 !border-0 rounded-full my-1" />
        )}
      </li>
    );
  },
);
CustomDot.displayName = "CustomDot";

const NewCarousel = ({
  streams,
  children,
  activeIndex,
  setActiveIndex,
  autoPlaySpeed,
  className = "",
  dotsClass = "",
  reverseDotColor,
  disableTimer,
}) => {
  const [progressIndex, setProgressIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const intervalRef = useRef(null);

  const resetTimeout = () => {
    intervalRef.current && clearInterval(intervalRef.current);
  };

  // Automatic slide transition (every n seconds)
  useEffect(() => {
    if (disableTimer) return;
    intervalRef.current = setInterval(() => {
      setActiveIndex(prevIndex => (prevIndex + 1) % streams.length);
      if (progressIndex < streams.length - 1) {
        setProgressIndex(prev => Number(prev) + 1);
        setProgress(0);
      } else {
        setProgressIndex(0);
        setProgress(0);
      }
    }, autoPlaySpeed);

    return resetTimeout;
  }, [
    streams.length,
    activeIndex,
    autoPlaySpeed,
    setActiveIndex,
    progressIndex,
  ]);

  return (
    <div className={`flex flex-col justify-center items-center ${className}`}>
      {React.Children.map(children, (child, index) =>
        React.cloneElement(child, {
          key: child.key || index,
        }),
      )}
      {/* Dots for navigation */}
      <ul
        className={`flex list-none gap-2 -mt-7 md:mt-0 4xl:mt-28 ${dotsClass}`}
      >
        <div className="md:invisible mr-2 flex rotate-180">
          <Image
            src={
              reverseDotColor
                ? "/images/rightArrow.svg"
                : "/images/carouselArrow.svg"
            }
            alt="Right Arrow"
            width={24}
            height={24}
            onClick={() => {
              if (activeIndex <= 0) {
                setActiveIndex(streams.length - 1);
              } else {
                setActiveIndex(prev => prev - 1);
              }
            }}
          />
        </div>
        {streams.map((_, index) => (
          <CustomDot
            autoPlaySpeed={autoPlaySpeed}
            index={index}
            progress={progress}
            setProgress={setProgress}
            key={index}
            active={activeIndex === index}
            reverse={reverseDotColor}
            disableTimer={disableTimer}
          />
        ))}
        <div className="md:invisible ml-2 flex">
          <Image
            src={
              reverseDotColor
                ? "/images/rightArrow.svg"
                : "/images/carouselArrow.svg"
            }
            alt="Right Arrow"
            width={24}
            height={24}
            onClick={() => {
              if (activeIndex >= streams.length - 1) {
                setActiveIndex(0);
              } else {
                setActiveIndex(prev => prev + 1);
              }
            }}
          />
        </div>
      </ul>
    </div>
  );
};

export default React.memo(NewCarousel);
